/* eslint-disable @typescript-eslint/no-magic-numbers */
import Color from 'color';

export const generatePaletteWithSize = (size: number, actionSecondaryColor?: string): string[] => {
  const secondaryActionColor = Color(actionSecondaryColor);
  return Array.from({
    length: size,
  }).map((_, idx) => {
    const step = Math.floor(60 / size);
    const stepFromMiddle = Math.ceil(idx / 2) * step;
    const lightness = 50 + stepFromMiddle * (idx % 2 === 0 ? -1 : 1);
    return secondaryActionColor.lightness(lightness).hex();
  });
};

export const generateColorPalette = (count: number): string[] => {
  const palette = [];

  for (let i = 0; i < count; i++) {
    const hue = (i * 360) / count; // evenly spaced around the circle
    const saturation = 90; // percent
    const value = 90; // percent

    // colors.hsv returns a color object, convert to hex
    const hex = Color.hsv(hue, saturation, value).hex();
    palette.push(hex);
  }

  return palette;
};

const FOREGROUND_PRIMARY_TEXT_CLASS = 'fill-foreground-primary-100';
const FOREGROUND_SECONDARY_TEXT_CLASS = 'fill-foreground-secondary-100';

// Picks whichever of the theme's primary/secondary foreground colours is more readable on top of the
// given background hex. The foreground tokens are theme-driven CSS variables, so we read their live
// values and compare actual contrast; when the DOM is unavailable (SSR) we fall back to a luminance
// heuristic (dark background -> light/secondary text).
export const pickReadableForegroundClass = (backgroundHex: string): string => {
  const background = Color(backgroundHex);

  if (typeof document !== 'undefined') {
    const rootStyle = getComputedStyle(document.documentElement);
    const primary = rootStyle.getPropertyValue('--color-foreground-primary-100').trim();
    const secondary = rootStyle.getPropertyValue('--color-foreground-secondary-100').trim();

    if (primary && secondary) {
      return background.contrast(Color(primary)) >= background.contrast(Color(secondary))
        ? FOREGROUND_PRIMARY_TEXT_CLASS
        : FOREGROUND_SECONDARY_TEXT_CLASS;
    }
  }

  return background.isDark() ? FOREGROUND_SECONDARY_TEXT_CLASS : FOREGROUND_PRIMARY_TEXT_CLASS;
};

export const generateColorPaletteWithReference = (count: number, referenceHex?: string): string[] => {
  const ref = Color(referenceHex).hsv();

  const palette = [];

  for (let i = 0; i < count; i++) {
    // evenly spaced hue rotation around the reference hue
    const hue = (ref.hue() + (i * 360) / count) % 360;

    // keep ref saturation and value, but slightly adjust saturation if desired
    const saturation = ref.saturationv(); // you can also multiply for stronger palettes
    const value = ref.value(); // same brightness as reference
    const hex = Color.hsv(hue, saturation, value).hex();
    palette.push(hex);
  }

  return palette;
};
